import { THEMES, type ThemeId } from './theme'

/**
 * Custom-accent contrast guard — the PURE MATH layer for batch 4's paid custom
 * accent hue. No DOM, no store, no UI: given a hue and a theme id, it returns a
 * guaranteed-readable accent color (≥ 4.5:1 WCAG AA against the least contrasty
 * surface) plus a lifted hover variant and an "r,g,b" string for rgba() vars.
 *
 * Ported from the design prototype
 * (`design system 7-11-26 → cosmic-app/appearance.jsx`, cfHexToRgb / cfLum /
 * cfContrast / cfHslToHex / cfGuardedAccent / CF_ACCENT_HUES). The theme's
 * page/card swatches are resolved from the web {@link THEMES} registry's
 * `swatch` field.
 *
 * DELIBERATE DEVIATION from the reference cfGuardedAccent: the reference tests
 * a light accent against the PAGE on dark themes, but on dark themes the lifted
 * card swatch is LESS contrasty than the page swatch for a light accent — so a
 * page-guarded accent can clear 4.5:1 vs the page while only landing ~4.2:1 on
 * a card, and the AA badge (derived from that ratio) would overstate (e.g. hue
 * 240 on midnight: 4.73 vs page, 4.20 vs card). The handoff prose promises the
 * "least-contrasty surface" and
 * an honest badge, which the reference code contradicts. We therefore test the
 * CARD on dark themes too (the genuinely least-contrasty normal surface for a
 * light accent) so the returned `ratio` — and thus the AA pill — is truthful.
 * accent.test.ts locks THIS corrected spec against an inline reference copy.
 */

interface Rgb {
  r: number
  g: number
  b: number
}

/** Parse a `#rrggbb` hex into its 0–255 channels. */
export function hexToRgb(hex: string): Rgb {
  const h = hex.replace('#', '')
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

/** WCAG 2.1 relative luminance of an RGB color. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const f = (c: number): number => {
    c /= 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b)
}

/** WCAG contrast ratio between two `#rrggbb` hexes (≥ 1). */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = relativeLuminance(hexToRgb(hexA))
  const b = relativeLuminance(hexToRgb(hexB))
  const [hi, lo] = a > b ? [a, b] : [b, a]
  return (hi + 0.05) / (lo + 0.05)
}

/** Convert HSL (h in degrees, s/l in 0–100) to a `#rrggbb` hex. */
export function hslToHex(h: number, s: number, l: number): string {
  s /= 100
  l /= 100
  const k = (n: number): number => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = (n: number): string => {
    const c = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
    return Math.round(255 * c)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

/** The result of the accent contrast guard. */
export interface GuardedAccent {
  /** The readable accent hex (as an "r,g,b" string is `rgb`). */
  fg: string
  /** The lifted hover/active variant hex. */
  fgHi: string
  /** `fg`'s channels as an "r,g,b" string for `rgba()` composition. */
  rgb: string
  /** The achieved contrast ratio of `fg` against the guard surface. */
  ratio: number
}

/**
 * The contrast guard. Given a hue (0–360) and a theme id, walk lightness toward
 * the readable side until the accent holds ≥ 4.5:1 (AA for normal text) against
 * the LEAST contrasty NORMAL surface — the lifted card — on BOTH light and dark
 * themes. (Deviation from the reference cfGuardedAccent, which tests the page on
 * dark themes and thereby overstates on-card contrast; see the module docstring.)
 * Returns { fg, fgHi, rgb, ratio } — `fgHi` is the hover variant.
 */
export function guardedAccent(hue: number, themeId: ThemeId): GuardedAccent {
  const theme = THEMES.find((t) => t.id === themeId) ?? THEMES[0]
  const { swatch } = theme
  const darkBg = relativeLuminance(hexToRgb(swatch.page)) < 0.35
  // Test/return against the card on every theme: it is the least-contrasty
  // normal surface for a light accent, so the AA badge stays honest.
  const against = swatch.card
  const sat = darkBg ? 42 : 58
  let fg: { hex: string; l: number } | null = null
  if (darkBg) {
    for (let l = 62; l <= 88; l += 2) {
      const c = hslToHex(hue, sat, l)
      if (contrastRatio(c, against) >= 4.5) {
        fg = { hex: c, l }
        break
      }
    }
    if (!fg) fg = { hex: hslToHex(hue, 20, 88), l: 88 }
  } else {
    for (let l = 44; l >= 12; l -= 2) {
      const c = hslToHex(hue, sat, l)
      if (contrastRatio(c, against) >= 4.5) {
        fg = { hex: c, l }
        break
      }
    }
    if (!fg) fg = { hex: hslToHex(hue, sat, 12), l: 12 }
  }
  const fgHi = hslToHex(
    hue,
    sat,
    darkBg ? Math.min(fg.l + 10, 92) : Math.max(fg.l - 10, 8),
  )
  const { r, g, b } = hexToRgb(fg.hex)
  return {
    fg: fg.hex,
    fgHi,
    rgb: `${r},${g},${b}`,
    ratio: contrastRatio(fg.hex, against),
  }
}

/**
 * Map a guarded accent to the nine production CSS custom properties. The
 * gold/slate legacy pairs and the semantic `--color-accent-fg*` vars all point
 * at the same guarded fg/fgHi so a custom hue drives every accent surface.
 */
export function accentVarMap(guard: GuardedAccent): Record<string, string> {
  const { fg, fgHi, rgb } = guard
  return {
    '--color-gold': fg,
    '--color-slate': fg,
    '--color-accent-fg': fg,
    '--color-gold-light': fgHi,
    '--color-slate-light': fgHi,
    '--color-accent-fg-hi': fgHi,
    '--color-accent-bg': `rgba(${rgb},0.13)`,
    '--color-accent-border': `rgba(${rgb},0.38)`,
    '--color-accent-glow': `0 6px 20px rgba(${rgb},0.22)`,
  }
}

/** Curated starting hues for the custom-accent picker row (verbatim order). */
export const ACCENT_HUES: readonly { h: number; label: string }[] = [
  { h: 350, label: 'Rose' },
  { h: 25, label: 'Ember' },
  { h: 90, label: 'Fern' },
  { h: 165, label: 'Sea' },
  { h: 210, label: 'Sky' },
  { h: 265, label: 'Iris' },
] as const
